// Permission keys and the role → permission matrix (doc 02 §3.3–3.4), as data.
// Used by the API guards and by the web `usePermission()` hook.

export const PERMISSIONS = [
  'platform.orgs.manage',
  'platform.users.manage',
  'platform.users.unlock',
  'platform.impersonate',
  'platform.templates.manage',
  'platform.health.view',
  'org.settings.manage',
  'org.users.manage',
  'org.users.directory',
  'org.audit.view',
  'org.impersonate',
  'datasources.manage',
  'datasources.view',
  'assets.manage',
  'assets.view',
  'equipment.manage',
  'equipment.view',
  'tests.manage',
  'tests.view',
  'seqlog.write',
  'signals.manage',
  'signals.view',
  'explorer.use',
  'data.export',
  'dashboards.create',
  'dashboards.edit',
  'dashboards.share',
  'dashboards.view',
  'dashboards.publishClient',
  'schematics.create',
  'schematics.edit',
  'schematics.share',
  'schematics.view',
  'schematics.publishClient',
  'templates.use',
  'templates.manage',
  'alerts.view',
  'alerts.manage',
  'assistant.use',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

export const ORG_ROLES = ['org_admin', 'editor', 'viewer', 'client'] as const;
export type OrgRole = (typeof ORG_ROLES)[number];

export const PLATFORM_ROLES = ['platform_admin', 'platform_support'] as const;
export type PlatformRole = (typeof PLATFORM_ROLES)[number];

const ORG_PERMISSIONS = PERMISSIONS.filter((p) => !p.startsWith('platform.'));

/**
 * Unconditional grants per org role. Permissions that depend on an org setting
 * (`org.impersonate`, `*.publishClient` for editors, `data.export` for clients) are added by
 * `resolvePermissions`.
 */
export const ROLE_PERMISSIONS: Record<OrgRole, readonly Permission[]> = {
  org_admin: ORG_PERMISSIONS.filter((p) => p !== 'org.impersonate'),
  editor: [
    'org.users.directory',
    'datasources.view',
    'assets.view',
    'equipment.view',
    'tests.manage',
    'tests.view',
    'seqlog.write',
    'signals.view',
    'explorer.use',
    'data.export',
    'dashboards.create',
    'dashboards.edit',
    'dashboards.share',
    'dashboards.view',
    'schematics.create',
    'schematics.edit',
    'schematics.share',
    'schematics.view',
    'templates.use',
    'templates.manage',
    'alerts.view',
    'alerts.manage',
    'assistant.use',
  ],
  viewer: [
    'assets.view',
    'equipment.view',
    'tests.view',
    'signals.view',
    'explorer.use',
    'data.export',
    'dashboards.view',
    'schematics.view',
    'alerts.view',
    'assistant.use',
  ],
  client: ['assets.view', 'tests.view', 'signals.view', 'dashboards.view', 'schematics.view', 'alerts.view'],
};

/** Platform Support: every `*.view` plus a short list of extras; no other mutations. */
export const PLATFORM_SUPPORT_PERMISSIONS: readonly Permission[] = [
  ...PERMISSIONS.filter((p) => p.endsWith('.view')),
  'org.users.directory',
  'explorer.use',
  'assistant.use',
  'platform.impersonate',
  'platform.users.unlock',
];

export interface PermissionContext {
  platformRole?: PlatformRole | null;
  orgRole?: OrgRole | null;
  orgSettings?: {
    allowOrgAdminImpersonation?: boolean;
    editorsCanPublishToClients?: boolean;
    clientExport?: boolean;
  };
  /** Per-membership override of the org `clientExport` setting (Client role only). */
  membershipClientExport?: boolean | null;
}

/** Effective permissions of a user inside one org (or at platform level when no org role). */
export function resolvePermissions(ctx: PermissionContext): Set<Permission> {
  if (ctx.platformRole === 'platform_admin') return new Set(PERMISSIONS);
  if (ctx.platformRole === 'platform_support') return new Set(PLATFORM_SUPPORT_PERMISSIONS);

  const result = new Set<Permission>();
  if (!ctx.orgRole) return result;
  for (const p of ROLE_PERMISSIONS[ctx.orgRole]) result.add(p);

  const settings = ctx.orgSettings ?? {};
  if (ctx.orgRole === 'org_admin' && settings.allowOrgAdminImpersonation) {
    result.add('org.impersonate');
  }
  if (ctx.orgRole === 'editor' && settings.editorsCanPublishToClients) {
    result.add('dashboards.publishClient');
    result.add('schematics.publishClient');
  }
  if (ctx.orgRole === 'client') {
    const allowed = ctx.membershipClientExport ?? settings.clientExport ?? false;
    if (allowed) result.add('data.export');
  }
  return result;
}

export function isPlatformDomain(email: string, domains: readonly string[]): boolean {
  const at = email.lastIndexOf('@');
  if (at < 0) return false;
  const domain = email.slice(at + 1).toLowerCase();
  return domains.some((d) => d.toLowerCase() === domain);
}

/** Roles that count against `plan.maxUsers`; Clients count against `maxClientUsers`. */
export const SEAT_ROLES: readonly OrgRole[] = ['org_admin', 'editor', 'viewer'];
